import { Injectable } from '@angular/core';

import { IRoomGeometry } from '@stair-craft/common/util';
import {
    BoxGeometry,
    CanvasTexture,
    Color,
    ConeGeometry,
    DoubleSide,
    ExtrudeGeometry,
    Group,
    LineBasicMaterial,
    Mesh,
    MeshStandardMaterial,
    Path,
    PlaneGeometry,
    Shape,
    Sprite,
    SpriteMaterial,
} from 'three';

import { FLOOR_GRID_MAJOR_OPACITY, FLOOR_GRID_MINOR_OPACITY, buildFloorGrid, createFloorGridMaterial } from './floor-grid';
import { ISceneGridColors, SCENE_GRID_FALLBACK, SCENE_GRID_MAJOR_FALLBACK } from './scene-background';

const WALL_COLOR: number = 0xf5f0e8;
const WALL_OPACITY: number = 0;
// A warm beige floor on the cool light-grey scene ground: the room footprint reads by hue.
const FLOOR_COLOR: number = 0xd4c9b0;
// A concrete grey a step darker than the scene ground, so the lit slab top keeps its outline on it.
const SLAB_COLOR: number = 0xc8c3b9;

@Injectable()
export class RoomMeshBuilder {
    readonly #wallMaterial: MeshStandardMaterial = new MeshStandardMaterial({
        color: new Color(WALL_COLOR),
        transparent: true,
        opacity: WALL_OPACITY,
        side: DoubleSide,
        roughness: 0.9,
        metalness: 0.0,
        // Transparent walls must not write depth; otherwise coplanar meshes
        // (railing posts against a stair-side wall) z-fight at the shared face.
        depthWrite: false,
    });

    readonly #floorMaterial: MeshStandardMaterial = new MeshStandardMaterial({
        color: new Color(FLOOR_COLOR),
        roughness: 0.8,
        metalness: 0.0,
        // Push floor slightly behind so objects standing on it
        // (post bases, stringer bottoms) always win the depth test.
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 2,
    });

    readonly #slabMaterial: MeshStandardMaterial = new MeshStandardMaterial({
        color: new Color(SLAB_COLOR),
        roughness: 0.8,
        metalness: 0.0,
        side: DoubleSide,
        // Push slab behind so coplanar meshes (stringer tops capped at slab
        // level, railings flush to the opening) always win the depth test.
        polygonOffset: true,
        polygonOffsetFactor: 2,
        polygonOffsetUnits: 4,
    });

    readonly #axisMaterialX: MeshStandardMaterial = new MeshStandardMaterial({
        color: new Color(0xcc2222),
        roughness: 0.5,
        metalness: 0.1,
    });
    readonly #axisMaterialY: MeshStandardMaterial = new MeshStandardMaterial({
        color: new Color(0x22aa44),
        roughness: 0.5,
        metalness: 0.1,
    });
    readonly #axisMaterialZ: MeshStandardMaterial = new MeshStandardMaterial({
        color: new Color(0x2266cc),
        roughness: 0.5,
        metalness: 0.1,
    });

    // Axis-label sprites carry a per-build CanvasTexture + SpriteMaterial that are not
    // shared like the materials above; tracked here so they can be disposed each rebuild.
    readonly #spriteMaterials: SpriteMaterial[] = [];

    // The two floor-grid line materials are shared by every rebuild; their colours come from the
    // page tokens through setGridColors() once the canvas exists, the fallbacks repeat the tokens.
    readonly #gridMinorMaterial: LineBasicMaterial = createFloorGridMaterial(SCENE_GRID_FALLBACK, FLOOR_GRID_MINOR_OPACITY);
    readonly #gridMajorMaterial: LineBasicMaterial = createFloorGridMaterial(SCENE_GRID_MAJOR_FALLBACK, FLOOR_GRID_MAJOR_OPACITY);

    /** Takes the grid line colours read from the page tokens. */
    public setGridColors(colors: ISceneGridColors): void {
        this.#gridMinorMaterial.color.set(colors.minor);
        this.#gridMajorMaterial.color.set(colors.major);
    }

    public buildAll(room: IRoomGeometry): Group {
        this.#disposeSprites();
        const group: Group = new Group();

        group.add(this.#buildFloor(room));
        group.add(buildFloorGrid(room, this.#gridMinorMaterial, this.#gridMajorMaterial));
        this.#buildWalls(room).forEach((wall: Mesh) => group.add(wall));
        group.add(this.#buildCeilingSlab(room));
        group.add(this.#buildAxisMarker(room));

        return group;
    }

    #buildFloor(room: IRoomGeometry): Mesh {
        const geo: PlaneGeometry = new PlaneGeometry(room.roomWidth, room.roomLength);
        const mesh: Mesh = new Mesh(geo, this.#floorMaterial);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.set(room.roomWidth / 2, 0, room.roomLength / 2);
        mesh.receiveShadow = true;
        mesh.name = 'room-floor';
        return mesh;
    }

    #buildWalls(room: IRoomGeometry): Mesh[] {
        const wallThickness: number = 20;
        const wallHeight: number = room.floorHeight + room.floorThickness;
        const walls: Mesh[] = [];

        // Back wall (Z = 0)
        const backGeo: BoxGeometry = new BoxGeometry(room.roomWidth, wallHeight, wallThickness);
        const backMesh: Mesh = new Mesh(backGeo, this.#wallMaterial);
        backMesh.position.set(room.roomWidth / 2, wallHeight / 2, -wallThickness / 2);
        backMesh.name = 'wall-back';
        walls.push(backMesh);

        // Left wall (X = 0)
        const leftGeo: BoxGeometry = new BoxGeometry(wallThickness, wallHeight, room.roomLength);
        const leftMesh: Mesh = new Mesh(leftGeo, this.#wallMaterial);
        leftMesh.position.set(-wallThickness / 2, wallHeight / 2, room.roomLength / 2);
        leftMesh.name = 'wall-left';
        walls.push(leftMesh);

        // Right wall (X = roomWidth)
        const rightGeo: BoxGeometry = new BoxGeometry(wallThickness, wallHeight, room.roomLength);
        const rightMesh: Mesh = new Mesh(rightGeo, this.#wallMaterial);
        rightMesh.position.set(room.roomWidth + wallThickness / 2, wallHeight / 2, room.roomLength / 2);
        rightMesh.name = 'wall-right';
        walls.push(rightMesh);

        // Front wall (Z = roomLength)
        const frontGeo: BoxGeometry = new BoxGeometry(room.roomWidth, wallHeight, wallThickness);
        const frontMesh: Mesh = new Mesh(frontGeo, this.#wallMaterial);
        frontMesh.position.set(room.roomWidth / 2, wallHeight / 2, room.roomLength + wallThickness / 2);
        frontMesh.name = 'wall-front';
        walls.push(frontMesh);

        return walls;
    }

    #buildCeilingSlab(room: IRoomGeometry): Mesh {
        const slabShape: Shape = new Shape();
        slabShape.moveTo(0, 0);
        slabShape.lineTo(room.roomWidth, 0);
        slabShape.lineTo(room.roomWidth, room.roomLength);
        slabShape.lineTo(0, room.roomLength);
        slabShape.lineTo(0, 0);

        const holePath: Path = new Path();
        if (room.openingCircular) {
            const cx: number = room.openingX + room.openingWidth / 2;
            const cz: number = room.openingZ + room.openingLength / 2;
            const radius: number = Math.min(room.openingWidth, room.openingLength) / 2;
            holePath.absarc(cx, cz, radius, 0, Math.PI * 2, false);
        } else {
            holePath.moveTo(room.openingX, room.openingZ);
            holePath.lineTo(room.openingX + room.openingWidth, room.openingZ);
            holePath.lineTo(room.openingX + room.openingWidth, room.openingZ + room.openingLength);
            holePath.lineTo(room.openingX, room.openingZ + room.openingLength);
            holePath.lineTo(room.openingX, room.openingZ);
        }
        slabShape.holes.push(holePath);

        const geo: ExtrudeGeometry = new ExtrudeGeometry(slabShape, {
            depth: room.floorThickness,
            bevelEnabled: false,
            // A round opening drawn in 96 chords stays within a millimetre of the circle the
            // spiral's exit platform follows; the default 12 left slots of several centimetres.
            curveSegments: 96,
        });

        const mesh: Mesh = new Mesh(geo, this.#slabMaterial);
        mesh.rotation.x = Math.PI / 2;
        // Slab: bottom at floorHeight, top at floorHeight + floorThickness (grows upward)
        mesh.position.set(0, room.floorHeight + room.floorThickness, 0);
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        mesh.name = 'ceiling-slab';

        return mesh;
    }

    #buildAxisMarker(room: IRoomGeometry): Group {
        const arrowLen: number = 600;
        const shaftSize: number = 12;
        const headLen: number = 100;
        const headWidth: number = 60;
        // Origin at the upper floor surface (slab top) — matches the plan view.
        const originY: number = room.floorHeight + room.floorThickness;
        const originZ: number = room.roomLength;

        const group: Group = new Group();
        group.name = 'axis-marker';

        const coneGeo: ConeGeometry = new ConeGeometry(headWidth / 2, headLen, 12);
        const arrowY: number = originY;

        // X axis (red, along +X — room width, under slab)
        const xShaftGeo: BoxGeometry = new BoxGeometry(arrowLen, shaftSize, shaftSize);
        const xShaft: Mesh = new Mesh(xShaftGeo, this.#axisMaterialX);
        xShaft.position.set(arrowLen / 2, arrowY, originZ);
        group.add(xShaft);

        const xHead: Mesh = new Mesh(coneGeo, this.#axisMaterialX);
        xHead.rotation.z = -Math.PI / 2;
        xHead.position.set(arrowLen + headLen / 2, arrowY, originZ);
        group.add(xHead);

        const xLabel: Sprite = this.#makeTextSprite('X', 0xcc2222);
        xLabel.position.set(arrowLen + headLen + 80, arrowY, originZ);
        group.add(xLabel);

        // Y axis (green, along -Z world = room length "into the room", under slab)
        const yShaftGeo: BoxGeometry = new BoxGeometry(shaftSize, shaftSize, arrowLen);
        const yShaft: Mesh = new Mesh(yShaftGeo, this.#axisMaterialY);
        yShaft.position.set(0, arrowY, originZ - arrowLen / 2);
        group.add(yShaft);

        const yHead: Mesh = new Mesh(coneGeo, this.#axisMaterialY);
        yHead.rotation.x = -Math.PI / 2;
        yHead.position.set(0, arrowY, originZ - arrowLen - headLen / 2);
        group.add(yHead);

        const yLabel: Sprite = this.#makeTextSprite('Y', 0x22aa44);
        yLabel.position.set(0, arrowY, originZ - arrowLen - headLen - 80);
        group.add(yLabel);

        // Z axis (blue, vertical — height above upper floor)
        const zShaftGeo: BoxGeometry = new BoxGeometry(shaftSize, arrowLen, shaftSize);
        const zShaft: Mesh = new Mesh(zShaftGeo, this.#axisMaterialZ);
        zShaft.position.set(0, originY + arrowLen / 2, originZ);
        group.add(zShaft);

        const zHead: Mesh = new Mesh(coneGeo, this.#axisMaterialZ);
        zHead.position.set(0, originY + arrowLen + headLen / 2, originZ);
        group.add(zHead);

        const zLabel: Sprite = this.#makeTextSprite('Z', 0x2266cc);
        zLabel.position.set(0, originY + arrowLen + headLen + 80, originZ);
        group.add(zLabel);

        return group;
    }

    #makeTextSprite(text: string, color: number): Sprite {
        const size: number = 128;
        const canvas: HTMLCanvasElement = globalThis.document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Failed to acquire 2D canvas context');
        }
        ctx.font = 'bold 96px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 14;
        ctx.strokeStyle = '#ffffff';
        ctx.strokeText(text, size / 2, size / 2);
        ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
        ctx.fillText(text, size / 2, size / 2);

        const texture: CanvasTexture = new CanvasTexture(canvas);
        const spriteMat: SpriteMaterial = new SpriteMaterial({ map: texture, depthTest: false });
        this.#spriteMaterials.push(spriteMat);
        const sprite: Sprite = new Sprite(spriteMat);
        sprite.scale.set(150, 150, 1);
        return sprite;
    }

    #disposeSprites(): void {
        for (const spriteMat of this.#spriteMaterials) {
            spriteMat.map?.dispose();
            spriteMat.dispose();
        }
        this.#spriteMaterials.length = 0;
    }

    public dispose(): void {
        this.#disposeSprites();
        this.#wallMaterial.dispose();
        this.#floorMaterial.dispose();
        this.#slabMaterial.dispose();
        this.#gridMinorMaterial.dispose();
        this.#gridMajorMaterial.dispose();
        this.#axisMaterialX.dispose();
        this.#axisMaterialY.dispose();
        this.#axisMaterialZ.dispose();
    }
}
